/**
 * Back Health Score, instrument v3.
 *
 * Nine scored questions in four constructs, 0 to 27, higher is healthier,
 * plus two unscored context questions. v3 is v2 with every option score
 * reflected (3 - v2), asked for by the clinical lead on 14 Sep 2026 so that a
 * rising score reads as improvement. The items, their order, the pathway
 * bands and the focus rules are v2's exactly; the test file carries the v2
 * scorer verbatim as an oracle and checks every one of the 4^9 answer
 * combinations against it through that reflection.
 *
 * One deliberate difference kept from the v2 port: the shipped scorer treated
 * a missing answer as the healthiest option. A clinical instrument should not
 * fill in blanks, so scoreBhs refuses an incomplete or out-of-range answer set.
 */

export const BHS_INSTRUMENT_VERSION = "v3";

export type Pathway = "strong" | "active" | "foundation";
export type BhsConstruct = "symptoms" | "confidence" | "lifestyle" | "recovery";

export interface BhsOption {
  label: string;
  score: number;
  key?: string;
}

export interface BhsItem {
  id: string;
  construct: BhsConstruct | "context";
  question: string;
  options: readonly BhsOption[];
}

export const BHS_ITEMS: readonly BhsItem[] = [
  {
    id: "workday_area",
    construct: "context",
    question: "Which area is most affected during your typical workday?",
    options: [
      { label: "Lower back", score: 0, key: "lower_back" },
      { label: "Neck and upper back", score: 0, key: "neck_upper_back" },
      { label: "Hips", score: 0, key: "hips" },
      { label: "No particular area", score: 0, key: "none" },
    ],
  },
  {
    id: "workday_timing",
    construct: "context",
    question: "When do you notice this most?",
    options: [
      { label: "Morning", score: 0, key: "morning" },
      { label: "During the workday", score: 0, key: "during_day" },
      { label: "End of the workday", score: 0, key: "end_of_day" },
      { label: "After long periods of sitting", score: 0, key: "after_sitting" },
      { label: "Throughout the day, no clear pattern", score: 0, key: "throughout_day" },
      { label: "I don't notice any issues", score: 0, key: "no_issues" },
    ],
  },
  {
    id: "q1_discomfort",
    construct: "symptoms",
    question: "Over the past two weeks, how would you describe your back discomfort on a typical day?",
    options: [
      { label: "No discomfort", score: 3 },
      { label: "Mild, easy to ignore", score: 2 },
      { label: "Moderate, noticeable but manageable", score: 1 },
      { label: "Significant, affects my concentration or comfort at work", score: 0 },
    ],
  },
  {
    id: "q2_frequency",
    construct: "symptoms",
    question: "How often does back discomfort affect your ability to work comfortably?",
    options: [
      { label: "Never", score: 3 },
      { label: "Occasionally", score: 2 },
      { label: "Most days", score: 1 },
      { label: "Every day", score: 0 },
    ],
  },
  {
    id: "q3_confidence",
    construct: "confidence",
    question:
      "How confident do you feel moving normally during your day, bending, reaching, getting up from your chair?",
    options: [
      { label: "Fully confident", score: 3 },
      { label: "Mostly confident, occasionally cautious", score: 2 },
      { label: "Hesitant with certain movements", score: 1 },
      { label: "I regularly avoid movements because of my back", score: 0 },
    ],
  },
  {
    id: "q4_work_adjustments",
    construct: "confidence",
    question:
      "Has your back affected how you work, for example sitting differently, avoiding certain tasks, or getting up less often than you should?",
    options: [
      { label: "Not at all", score: 3 },
      { label: "Occasionally", score: 2 },
      { label: "Yes, I have changed how I work because of it", score: 1 },
      { label: "Yes, significantly", score: 0 },
    ],
  },
  {
    id: "q5_sitting",
    construct: "lifestyle",
    question: "How long do you typically sit before getting up during your workday?",
    options: [
      { label: "Less than 30 minutes", score: 3, key: "under_30_min" },
      { label: "30 to 60 minutes", score: 2, key: "30_60_min" },
      { label: "60 to 90 minutes", score: 1, key: "60_90_min" },
      { label: "More than 90 minutes", score: 0, key: "over_90_min" },
    ],
  },
  {
    id: "q6_activity",
    construct: "lifestyle",
    question: "How many days a week do you do 20 or more minutes of physical activity outside work?",
    options: [
      { label: "4 or more", score: 3 },
      { label: "2 to 3", score: 2 },
      { label: "1", score: 1 },
      { label: "None", score: 0 },
    ],
  },
  {
    id: "q7_settle",
    construct: "recovery",
    question: "When your back feels stiff or uncomfortable, how quickly does it usually settle?",
    options: [
      { label: "I rarely get stiffness or discomfort", score: 3 },
      { label: "Settles within an hour or two", score: 2 },
      { label: "Takes most of the day", score: 1 },
      { label: "Takes several days or never fully settles", score: 0 },
    ],
  },
  {
    id: "q8_movement_response",
    construct: "recovery",
    question: "When you move around, stretch, or go for a walk, what happens to your back?",
    options: [
      { label: "Always feels better", score: 3 },
      { label: "Usually helps", score: 2 },
      { label: "Makes no difference", score: 1 },
      { label: "Sometimes makes it worse", score: 0 },
    ],
  },
  {
    id: "q9_pattern",
    construct: "recovery",
    question: "How would you describe the pattern of your back over the past three months?",
    options: [
      { label: "Consistently fine", score: 3 },
      { label: "Occasional stiffness, quickly resolved", score: 2 },
      { label: "Recurring episodes that come and go", score: 1 },
      { label: "Persistent or getting gradually worse", score: 0 },
    ],
  },
];

export const BHS_SCORED_IDS: readonly string[] = BHS_ITEMS.filter(
  (i) => i.construct !== "context",
).map((i) => i.id);

export const BHS_SUB_MAX: Readonly<Record<BhsConstruct, number>> = {
  symptoms: 6,
  confidence: 6,
  lifestyle: 6,
  recovery: 9,
};
export const BHS_TOTAL_MAX = 27;

/** Scored answers keyed by item id; the value is the chosen option's score. */
export type BhsAnswers = Readonly<Record<string, number>>;

export type FocusTag =
  | "mobility_focus"
  | "desk_focus"
  | "confidence_focus"
  | "recovery_focus"
  | "activity_focus";

export interface BhsResult {
  instrumentVersion: typeof BHS_INSTRUMENT_VERSION;
  subs: Record<BhsConstruct, number>;
  total: number;
  pathway: Pathway;
  focusTags: FocusTag[];
  highSittingLoad: boolean;
}

function answer(answers: BhsAnswers, id: string): number {
  const v = answers[id];
  if (v === undefined) throw new RangeError(`missing answer: ${id}`);
  const item = BHS_ITEMS.find((i) => i.id === id);
  if (!item || !item.options.some((o) => o.score === v)) {
    throw new RangeError(`answer out of range for ${id}: ${String(v)}`);
  }
  return v;
}

const RANK: Record<Pathway, number> = { strong: 2, active: 1, foundation: 0 };

/** The higher-need of two pathways. Overrides only ever move that way. */
function atLeast(a: Pathway, b: Pathway): Pathway {
  return RANK[a] <= RANK[b] ? a : b;
}

/**
 * Bands on the v3 total: 19 to 27 strong, 9 to 18 active, 0 to 8 foundation.
 * The overrides are v2's, read on the reflected scale: a v2 answer of 3 (the
 * worst option) is a v3 answer of 0.
 */
export function assignPathway(total: number, answers: BhsAnswers): Pathway {
  let pathway: Pathway = total >= 19 ? "strong" : total >= 9 ? "active" : "foundation";
  const q2 = answer(answers, "q2_frequency");
  const q3 = answer(answers, "q3_confidence");
  const q4 = answer(answers, "q4_work_adjustments");
  const q7 = answer(answers, "q7_settle");
  const q8 = answer(answers, "q8_movement_response");
  const q9 = answer(answers, "q9_pattern");
  if (q7 === 0 && (q8 === 0 || q8 === 1)) pathway = atLeast(pathway, "foundation");
  if (q3 === 0 && (q4 === 0 || q4 === 1)) pathway = atLeast(pathway, "foundation");
  if (q2 === 0 && (q4 === 0 || q4 === 1)) pathway = atLeast(pathway, "active");
  if (q9 === 0) pathway = atLeast(pathway, "active");
  return pathway;
}

export function answerKeyFor(id: string, score: number | undefined): string | undefined {
  if (score === undefined) return undefined;
  const item = BHS_ITEMS.find((i) => i.id === id);
  return item?.options.find((o) => o.score === score && o.key)?.key;
}

export function scoreBhs(answers: BhsAnswers): BhsResult {
  const a = (id: string) => answer(answers, id);
  const subs: Record<BhsConstruct, number> = {
    symptoms: a("q1_discomfort") + a("q2_frequency"),
    confidence: a("q3_confidence") + a("q4_work_adjustments"),
    lifestyle: a("q5_sitting") + a("q6_activity"),
    recovery: a("q7_settle") + a("q8_movement_response") + a("q9_pattern"),
  };
  const total = subs.symptoms + subs.confidence + subs.lifestyle + subs.recovery;
  const pathway = assignPathway(total, answers);

  const q3 = a("q3_confidence");
  const q5Key = answerKeyFor("q5_sitting", a("q5_sitting"));
  const q6 = a("q6_activity");
  // Focus rules are v2's on the reflected scale; weights are the v2 sub-scores
  // (how much is getting in the way) so the ordering is unchanged.
  const load = (c: BhsConstruct) => BHS_SUB_MAX[c] - subs[c];
  const tags: { tag: FocusTag; weight: number }[] = [];
  if (load("confidence") > 3) tags.push({ tag: "mobility_focus", weight: load("confidence") });
  if (q5Key === "60_90_min" || q5Key === "over_90_min") {
    tags.push({ tag: "desk_focus", weight: load("lifestyle") });
  }
  if (q3 === 0 || q3 === 1) tags.push({ tag: "confidence_focus", weight: load("confidence") });
  if (load("recovery") > 5) tags.push({ tag: "recovery_focus", weight: load("recovery") });
  if (q6 === 0 || q6 === 1) tags.push({ tag: "activity_focus", weight: load("lifestyle") });
  tags.sort((x, y) => y.weight - x.weight);

  return {
    instrumentVersion: BHS_INSTRUMENT_VERSION,
    subs,
    total,
    pathway,
    focusTags: tags.slice(0, 2).map((t) => t.tag),
    highSittingLoad: q5Key === "60_90_min" || q5Key === "over_90_min",
  };
}

/**
 * Work impact, asked after the nine scored questions and never scored (D-51).
 * W1 is how much the back got in the way of getting work done in the past
 * seven days, 0 to 10. W2, asked only when W1 is above 0, is how much work
 * was missed; a W1 of 0 is read as none missed. They exist for the employer's
 * quarterly figures, never as a claim about what Back Coach caused.
 */
export const WORK_PRODUCTIVITY_QUESTION =
  "In the past 7 days, how much did your back affect how much you got done at work?";
export const WORK_PRODUCTIVITY_ANCHORS = { low: "No effect", high: "I couldn't work" } as const;
export const WORK_MISSED_QUESTION = "In the past 7 days, how much work did you miss because of your back?";

export type WorkMissed = "none" | "under_half_day" | "half_to_one_day" | "one_to_two_days" | "over_two_days";

/** Days are each band's midpoint; the open top band is counted as 2.5, which understates it. */
export const WORK_MISSED_OPTIONS: readonly { key: WorkMissed; label: string; days: number }[] = [
  { key: "none", label: "None", days: 0 },
  { key: "under_half_day", label: "Less than half a day", days: 0.25 },
  { key: "half_to_one_day", label: "Half a day to a day", days: 0.75 },
  { key: "one_to_two_days", label: "1 to 2 days", days: 1.5 },
  { key: "over_two_days", label: "More than 2 days", days: 2.5 },
];

export interface WorkImpact {
  /** 0 to 10. */
  productivity: number;
  /** Null when W1 was 0 and W2 was not asked. */
  missed: WorkMissed | null;
}

/** Refuses an answer set the questions could not have produced. */
export function checkWorkImpact(w: WorkImpact): WorkImpact {
  if (!Number.isInteger(w.productivity) || w.productivity < 0 || w.productivity > 10) {
    throw new RangeError(`work impact out of range: ${String(w.productivity)}`);
  }
  if (w.productivity === 0) return { productivity: 0, missed: null };
  if (w.missed === null || !WORK_MISSED_OPTIONS.some((o) => o.key === w.missed)) {
    throw new RangeError("work missed is required when work was affected");
  }
  return w;
}

/** Days of work missed in the week, from the band. */
export function workDaysMissed(w: WorkImpact): number {
  if (w.missed === null) return 0;
  return WORK_MISSED_OPTIONS.find((o) => o.key === w.missed)?.days ?? 0;
}
